import React, { useEffect, useState } from 'react';
import { apiGet } from '../../lib/api';
import { User, Calendar, Receipt, CheckCircle2, GraduationCap, Boxes, Plane } from 'lucide-react';
import { inrCompact } from '../../lib/format';
import MyITRequests from '../it/MyITRequests';

const fmt = (s) => s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export default function MyProfile() {
  const [profile, setProfile] = useState(null);
  const [balance, setBalance] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const me = await apiGet('/hr/me');
      setProfile(me);
      if (me?.linked) {
        const [bal, ps, att] = await Promise.all([
          apiGet('/hr/me/leave-balance').catch(() => ({ balance: [] })),
          apiGet('/hr/me/payslips').catch(() => ({ payslips: [] })),
          apiGet(`/hr/me/attendance?month=${month}`).catch(() => ({ attendance: [] })),
        ]);
        setBalance(bal.balance || []);
        setPayslips(ps.payslips || []);
        setAttendance(att.attendance || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (profile?.linked) apiGet(`/hr/me/attendance?month=${month}`).then(r => setAttendance(r.attendance || [])); }, [month, profile]);

  if (loading) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  if (!profile?.linked) {
    return (
      <div>
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center">
          <User size={32} className="text-slate-400 mx-auto mb-3" />
          <h2 className="font-display text-xl font-bold mb-2">No Employee Profile Linked</h2>
          <p className="text-sm text-slate-600">Your account ({profile?.user_email}) is not yet linked to an employee record. Please contact HR to complete your onboarding.</p>
        </div>
      </div>
    );
  }

  const emp = profile.employee;
  const todayStr = new Date().toISOString().slice(0, 10);
  const todayAtt = attendance.find(a => a.date === todayStr);

  return (
    <div>
      <div className="mb-6">
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">Employee Self-Service</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">My Profile</h1>
        <p className="text-sm text-slate-600 mt-1">View your details, leave balance, payslips and attendance summary. Need to fix attendance? Use the <strong>Regularization</strong> request from the menu.</p>
        <a href="/app/hr/my-training" data-testid="my-training-link" className="inline-flex items-center gap-1.5 mt-3 text-xs font-display font-bold uppercase tracking-wider text-[#1F7A6E] hover:underline">
          <GraduationCap size={14} /> View My Training & Certifications
        </a>
        <div className="flex flex-wrap gap-4 mt-2">
          <a href="/app/hr/my-assets" data-testid="my-assets-link" className="inline-flex items-center gap-1.5 text-xs font-display font-bold uppercase tracking-wider text-[#1F7A6E] hover:underline">
            <Boxes size={14} /> My Assets
          </a>
          <a href="/app/hr/my-travel" data-testid="my-travel-link" className="inline-flex items-center gap-1.5 text-xs font-display font-bold uppercase tracking-wider text-[#1F7A6E] hover:underline">
            <Plane size={14} /> My Travel
          </a>
        </div>
      </div>

      {/* Top: Identity */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6">
        <div className="flex flex-wrap items-center gap-6">
          <div className="w-16 h-16 rounded-sm bg-[#1F7A6E] text-white flex items-center justify-center font-display font-extrabold text-xl">
            {(emp.name || 'E').slice(0, 2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-extrabold text-2xl text-[#0F172A]">{emp.name}</div>
            <div className="text-sm text-slate-600 mt-0.5">{emp.designation} · {emp.department || 'No dept'}</div>
            <div className="text-xs text-slate-500 mt-1">Emp ID: {emp.emp_id} · DOJ: {fmt(emp.doj)}</div>
          </div>
        </div>
        {todayAtt && (
          <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-600 flex flex-wrap gap-4">
            <span className="inline-flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-600" /> Today: <strong>{todayAtt.status}</strong></span>
            {todayAtt.check_in && <span>In: <strong>{todayAtt.check_in}</strong></span>}
            {todayAtt.check_out && <span>Out: <strong>{todayAtt.check_out}</strong></span>}
          </div>
        )}
      </div>

      {/* Leave balance */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6">
        <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><Calendar size={16} /> Leave Balance · {new Date().getFullYear()}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {balance.map(b => (
            <div key={b.leave_type} className="border border-slate-200 rounded-sm p-4">
              <div className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">{b.leave_type}</div>
              <div className="flex items-baseline gap-1 mt-1">
                <span className="font-display text-2xl font-extrabold text-[#1F7A6E]">{b.remaining}</span>
                <span className="text-xs text-slate-500">/ {b.quota}</span>
              </div>
              <div className="text-[10px] text-slate-500 mt-1">{b.used} used</div>
            </div>
          ))}
        </div>
      </div>

      {/* Payslips */}
      <div className="bg-white border border-slate-200 rounded-sm mb-6">
        <div className="px-5 py-3 border-b border-slate-200">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><Receipt size={16} /> Payslips</h3>
        </div>
        <table className="w-full dense-table">
          <thead><tr><th>Month</th><th>Gross</th><th>Deductions</th><th>Net Pay</th><th>Status</th></tr></thead>
          <tbody>
            {payslips.map(p => (
              <tr key={p.id}>
                <td className="font-display font-semibold">{p.month}</td>
                <td className="text-xs">{p.gross ? inrCompact(p.gross) : '—'}</td>
                <td className="text-xs">{p.deductions ? inrCompact(p.deductions) : '—'}</td>
                <td className="font-display font-bold text-[#1F7A6E]">{p.net_pay ? inrCompact(p.net_pay) : '—'}</td>
                <td><span className={`tag ${p.status === 'Paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{p.status || 'Pending'}</span></td>
              </tr>
            ))}
            {!payslips.length && <tr><td colSpan="5" className="p-6 text-center text-sm text-slate-500">No payslips yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Attendance month */}
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">My Attendance</h3>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="input text-sm w-40" />
        </div>
        {attendance.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-500">No attendance recorded for this month.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {attendance.slice().reverse().map(a => (
              <li key={a.date} className="px-5 py-2.5 flex items-center gap-4 text-sm">
                <span className="font-display font-semibold w-24">{a.date}</span>
                <span className={`tag ${a.status === 'P' ? 'bg-emerald-100 text-emerald-700' : a.status === 'A' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}`}>{a.status}</span>
                {a.check_in && <span className="text-xs text-slate-500">In {a.check_in}</span>}
                {a.check_out && <span className="text-xs text-slate-500">· Out {a.check_out}</span>}
                {a.check_in_site && <span className="text-xs text-slate-500">· {a.check_in_site}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-5 mt-6" data-testid="profile-it-requests">
        <MyITRequests />
      </div>
    </div>
  );
}
